import type { PeerUsage } from '@voxa/shared';
import type { Pricing } from './capabilities.ts';

/**
 * What a call has cost so far, as an estimate (task 6).
 *
 * Neither provider reports a price per session, so this is usage multiplied
 * by the rates the server was configured with — published prices, not an
 * invoice. It is labelled as an estimate wherever it is shown, and it counts
 * what is billed rather than what was useful: a translation session costs for
 * as long as it is open, speaking or not.
 */

export type Usage = Omit<PeerUsage, 'type'>;

export const NO_USAGE: Usage = { translateSeconds: 0, transcribeSeconds: 0, voiceChars: 0 };

export interface Cost {
  openaiUsd: number;
  voiceUsd: number;
  voiceCredits: number;
  totalUsd: number;
}

export function costOf(usages: readonly Usage[], pricing: Pricing): Cost {
  let openaiUsd = 0;
  let voiceCredits = 0;
  for (const u of usages) {
    openaiUsd += (u.translateSeconds / 60) * pricing.translateUsdPerMin;
    openaiUsd += (u.transcribeSeconds / 60) * pricing.transcribeUsdPerMin;
    voiceCredits += u.voiceChars * pricing.voiceCreditsPerChar;
  }
  const voiceUsd = (voiceCredits / 1000) * pricing.voiceUsdPer1kCredits;
  return {
    openaiUsd,
    voiceUsd,
    voiceCredits: Math.round(voiceCredits),
    totalUsd: openaiUsd + voiceUsd,
  };
}

/** Cents are too coarse for a call that costs three of them; tenths of a cent are not. */
export const usd = (n: number): string => `$${n.toFixed(n < 1 ? 3 : 2)}`;
